// ForgotPasswordForm.js
// WEST-599: step 1 of the reset flow. Takes a username OR email (same rule as login) and always
// shows the same confirmation, whatever the server did — the page must not reveal whether an
// account or an email address exists.
import React, { useState } from 'react';
import { Link, useParams, useOutletContext } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import './LoginForm.css';

const GENERIC_MESSAGE =
  'If that account exists and has an email address on file, a password reset link is on its way. ' +
  'The link works once and expires in 10 minutes. Check your spam folder if it does not arrive.';

const ForgotPasswordForm = () => {
  const { customerId } = useParams();
  const { dataClient } = useOutletContext();

  const [identifier, setIdentifier] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  if (!dataClient) {
    return <LoadingSpinner />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const trimmed = identifier.trim();
    if (!trimmed) {
      setError('Please enter your username or email address.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_PROXY_URL}/api/auth/forgot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: trimmed, customer_id: customerId }),
      });

      if (response.status === 429) {
        setError('Too many requests. Please wait a few minutes and try again.');
        return;
      }
      // Any other outcome, including a server error, shows the same message by design.
      setDone(true);
    } catch (err) {
      console.error('Forgot password request failed:', err);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="user-form">
        <div className="message">{GENERIC_MESSAGE}</div>
        <p>
          <Link to={`/customer/${customerId}/login`}>Back to sign in</Link>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <table className="tableMain" border="0" width="100%" cellPadding="2" cellSpacing="2">
        <tbody>
          <tr>
            <td colSpan="2">
              <p>
                Enter the username or email address for your account and we will email you a link
                to choose a new password.
              </p>
              {error && <div className="error">{error}</div>}
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Username or email:</td>
            <td>
              <input
                className="fieldsSm"
                type="text"
                name="identifier"
                autoComplete="username"
                autoFocus
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <input type="submit" value={submitting ? 'Sending…' : 'Send reset link'} disabled={submitting} />
              {' '}
              <Link to={`/customer/${customerId}/login`}>Cancel</Link>
            </td>
          </tr>
        </tbody>
      </table>
    </form>
  );
};

export default ForgotPasswordForm;
